import {
  CALL_METRIC_LABEL,
  formatMetric,
  labelledIndices,
  linePath,
  offsetLabel,
  seriesFor,
  topOf,
  type CallMetric,
  type ChartBox,
} from '../lib/callChart.ts';
import type { AdminCallSample } from '../api/client.ts';
import './AdminCallChart.css';

/**
 * One side of one call, as a line per thing worth watching.
 *
 * Two panels rather than one shared plot, because the two sides of a call are
 * two measurements of the same event and reading them stacked is the whole
 * point: «у него исходящее упало, у меня входящее не менялось» is a specific
 * diagnosis, and it is invisible when both are averaged into one curve.
 *
 * Everything a person could get wrong by eye lives in lib/callChart.ts — the
 * scale, the breaks in a line where a side reported nothing, the arithmetic of
 * the axis. This file is geometry and colour.
 */

/* The plot in viewBox units; the SVG scales to the panel's width. */
const W = 720;
const H = 190;
/* The left margin is the widest axis label plus air: «1.5 Мбит/с» at this font
   is about 66 units wide, and a label that does not fit is clipped by the
   viewBox rather than wrapping — which reads on screen as a scale of «00». */
const BOX: ChartBox = { left: 78, right: W - 8, top: 10, bottom: 150 };
const LABEL_Y = 172;

/** As many lines as the widest metric has; the CSS names them in this order. */
const TONES = ['a', 'b', 'c', 'd'];

export function AdminCallChart({
  samples,
  metric,
  startedAt,
}: {
  samples: AdminCallSample[];
  metric: CallMetric;
  startedAt: number;
}) {
  if (samples.length === 0) {
    return <p className="admin-note">Эта сторона не прислала ни одного замера.</p>;
  }

  const series = seriesFor(metric, samples);
  const top = topOf(series);
  const labels = labelledIndices(samples.length);
  const span = Math.max(1, samples.length - 1);

  return (
    <div className="call-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="call-chart-svg" role="img" aria-label={CALL_METRIC_LABEL[metric]}>
        {[0, 0.5, 1].map((fraction) => {
          const y = BOX.bottom - (BOX.bottom - BOX.top) * fraction;
          return (
            <g key={fraction}>
              <line x1={BOX.left} x2={BOX.right} y1={y} y2={y} className="call-chart-grid" />
              <text x={BOX.left - 8} y={y + 4} className="call-chart-axis" textAnchor="end">
                {formatMetric(metric, top * fraction)}
              </text>
            </g>
          );
        })}

        {series.map((line, index) => (
          <path
            key={line.key}
            d={linePath(line.values, top, BOX)}
            className={`call-chart-line call-chart-line-${TONES[index] ?? 'a'}`}
          />
        ))}

        {labels.map((index) => (
          <text
            key={index}
            x={BOX.left + ((BOX.right - BOX.left) * index) / span}
            y={LABEL_Y}
            className="call-chart-axis"
            textAnchor="middle"
          >
            {offsetLabel(samples[index]!.at, startedAt)}
          </text>
        ))}
      </svg>

      <div className="call-chart-legend">
        {series.map((line, index) => (
          <span key={line.key} className="call-chart-key">
            <i className={`call-chart-swatch call-chart-swatch-${TONES[index] ?? 'a'}`} />
            {line.label}
          </span>
        ))}
      </div>
    </div>
  );
}
